import { useState, useEffect, useRef, forwardRef, useImperativeHandle, ForwardedRef } from 'react'
import { Card ,CardHeader,Collapse,CardBody,Input, Label, FormGroup, Form, FormFeedback, Row, Col} from 'reactstrap';
import { ICategory, IOrg, IOrgInput, isCategory } from "../../../types/org";
import { ApolloError, useApolloClient, } from "@apollo/client";

import { ORG_VALUE_LISTS } from "../../../graphql/valuelist-gql";
import { IValueListLight} from "../../../types/valuelist";
import useOrgTypes from '../../../hook/useOrgTypes';
import AddressDetails, { AddressHandler } from '../contact-info/org-address/address-details';
import Select from '../../common/select/select';
import Textarea, { TextareaHandler } from '../../common/textarea/textarea';
import { validOrgName, validOrgNameErrorMessage } from '../../../utils/commons';
import { acronym } from '../../../utils/acronym';
import AuthWrapper from '../../common/auth-wrapper';
import useAuth from '../../../hook/useAuth';
import { UserRoleType } from '../../../types/constants';

export type EditOrgHandler = {
    getEditedOrg: () => IOrgInput;
    validate: (inclMandatoryCheck: boolean) => boolean;
    setError: (error: ApolloError) => ApolloError;
};

type OrgDetailProps = {
    parent: ICategory | IOrg;
    isAddressMandatory: boolean;
    onChange: ()=>void;
    autoFocus?: boolean;
}



const OrgCreateDetail = forwardRef<EditOrgHandler,OrgDetailProps>((props: OrgDetailProps, ref: ForwardedRef<any>) => {
    const { parent, isAddressMandatory, onChange, autoFocus} = props

    const { orgTypes, defaultOrgType } = useOrgTypes(parent)
    const [nameChanged,setNameChanged] = useState<boolean>(false)
    const [typeChanged,setTypeChanged] = useState<boolean>(false)
    const {isAdmin, isCoordinator,isFrenchTranslator} = useAuth()

    //console.log("defaultOrgType:",defaultOrgType)

    const [editedOrg, setEditedOrg] = useState<Partial<IOrgInput>>({
        orgName: '',
        orgTypeId: defaultOrgType?.id||0,
        orgPrefixId: 0,
        orgAcronym: '',
        description: ''
    })

    const addressRef = useRef<AddressHandler>(null)
    const descRef = useRef<TextareaHandler>(null)

    const [orgNameError,setOrgNameError] = useState<string>('')
    const [acronymError,setAcronymError] = useState<string>('')
    const [orgTypeError,setOrgTypeError] = useState<string>('')

    const client = useApolloClient()
    const { orgValueLists } =  client.readQuery({query: ORG_VALUE_LISTS})

    useEffect(()=>{
        if(defaultOrgType)
            setEditedOrg({...editedOrg,orgTypeId:defaultOrgType.id})
    },[defaultOrgType])

    useEffect(() => {
        onChange()
    },[editedOrg,onChange]);

    useImperativeHandle(ref, () => ({
        getEditedOrg() {
            return {...editedOrg, address: addressRef?.current?.getAddress()}
        },
        validate(inclMandatoryCheck: boolean) {

            let isValid = !orgNameError && !acronymError && addressRef.current?.validate(inclMandatoryCheck)
            //console.log("isValid:",isValid)
            if (isValid && inclMandatoryCheck) {
                if (!editedOrg?.orgName?.trim()) {
                    //console.log("editedOrg:",editedOrg)
                    nameChanged &&
                        setOrgNameError('Name is required.')
                    isValid = false
                }
                if (!editedOrg?.orgTypeId) {
                    typeChanged &&
                        setOrgTypeError('Type is required.')
                    isValid = false
                }
                if (!addressRef?.current?.validate(true)) {
                    isValid = false
                }
            }

            return isValid
        },
        setError(error: ApolloError) {
            if(!error) return;

            if (error.graphQLErrors && error.graphQLErrors.length > 0) {
                error.graphQLErrors.forEach((graphQLError) => {
                    // if this happens, there might be a validation mismatch between the client and server
                    if (graphQLError.extensions && graphQLError.extensions.field === "orgName") {
                        setOrgNameError(error.message);
                    }
                });
            }
        }
    }), [editedOrg,setOrgNameError])

    const changeOrgName = (event: any) => {
        setNameChanged(true)
        let value = event.target.value
        if (value && value.length>200) {
            setOrgNameError('This value is too long. It should have 200 characters or fewer.')
        } else if (value && !validOrgName(value)) {
            setOrgNameError(validOrgNameErrorMessage)
        } else if (value.trim()) {
            setOrgNameError('')
        }
        setEditedOrg({ ...editedOrg, orgName: value, orgAcronym: acronym(value)})
    }

    const changeOrgType = (event: any) => {
        setTypeChanged(true)
        if (event.target.value) {
            setOrgTypeError('')
        }
        setEditedOrg({ ...editedOrg, orgTypeId: event.target.value })
    }

    const changeOrgDesc = (value: string) => {
        setEditedOrg({ ...editedOrg, description: value })
    }

    const changeOrgPrefix = (event: any) => {
        setEditedOrg({...editedOrg, orgPrefixId: event.target.value})
    }

    const changeOrgAcronym = (event: any) => {
        let value = event.target.value
        if (value && value.length > 30) {
            setAcronymError('This value is too long. It should have 30 characters or fewer.')
        } else {
            setAcronymError('')
        }
        setEditedOrg({...editedOrg, orgAcronym: value})
    }

    return (
        <div className="org-detail">

            <div className='row' style={{backgroundColor:'#fed992',paddingTop:'10px',border: '1px solid var(--bs-border-color-translucent)',borderRadius:'var(--bs-border-radius)'}}>
                <p  tabIndex={0}>You are creating a child organization under the parent organization:
                <br/><b><i>{isCategory(parent) ? parent.categoryName : parent.orgName}</i></b></p>
            </div>
            <Form>
                <Card style={{ marginBottom: '1rem' }} >
                    <CardHeader >Details</CardHeader>
                    <Collapse isOpen={true}>
                        <CardBody style={{paddingLeft:'0px',paddingRight:'0px'}}>
                        <Row>
                            <Col md={3}> <Label for="orgName">Name: (required)</Label></Col>
                            <Col md={9}>
                                <Input id="orgName" type="text" style={{ width: '100%' }} value={editedOrg?.orgName || ''} onChange={changeOrgName} required
                                    placeholder='Enter a name for the new organization' invalid={!!orgNameError} autoFocus={autoFocus}/>
                                <FormFeedback>{orgNameError}</FormFeedback>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={3}>
                                <Label for="orgtype">Type: (required)</Label>
                            </Col>
                            <Col md={9}>
                                <Select id='orgtype' name="orgtype" value={editedOrg?.orgTypeId || ''} onChange={changeOrgType} style={{ width: '100%' }} invalid={!!orgTypeError} required>
                                    <option value="" disabled hidden>Select a type...</option>
                                    {orgTypes?.map(item => <option key={item.id} value={item.id}>{item.label}</option>)}
                                </Select>
                                <FormFeedback>{orgTypeError}</FormFeedback>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={3}><Label role="region" aria-label='Prefix' for="orgprefix">Prefix:</Label></Col>
                            <Col md={9}>
                                <AuthWrapper role={[UserRoleType.ADMINISTRATOR]}>
                                    {(authorized: boolean) =>
                                        <Select id="orgprefix" value={editedOrg?.orgPrefixId || ''} onChange={changeOrgPrefix} style={{ width: '100%' }} disabled={!authorized}>
                                            <option value="" disabled hidden>Select a prefix...</option>
                                            <option value=''></option>
                                            {orgValueLists.orgPrefixes.map((item: IValueListLight) => <option key={item.id} value={item.id}>{item.label}</option>)}
                                        </Select>
                                    }
                                </AuthWrapper>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={3}><Label role="region" aria-label='orgAcronym' for="orgAcronym">Acronym:</Label></Col>
                            <Col md={9}>
                                <Input id='orgAcronym' aria-labelledby="orgAcronym"  type="text" style={{ width: '100%' }} value={editedOrg?.orgAcronym || ''} onChange={changeOrgAcronym}
                                placeholder='Enter an acronym for the new organization' invalid={!!acronymError}/>
                                <FormFeedback>{acronymError}</FormFeedback>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={3}><Label role="region" aria-label='orgDescription' for="orgDescription">Description:</Label></Col>
                            <Col md={9}>
                                <Textarea
                                    id = 'orgDescription'
                                    ref={descRef}
                                    maxLength={1500}
                                    aria-labelledby="orgDescription"
                                    rows={4} style={{ width: '100%' }}
                                    value={editedOrg?.description || ''}
                                    onValueChange={changeOrgDesc}
                                    placeholder='Enter a description for the new organization'
                                />
                            </Col>
                        </Row>
                        </CardBody>
                    </Collapse>
                </Card>
                <AddressDetails ref={addressRef} mandatory={isAddressMandatory} onChange={onChange}/>
            </Form>
        </div>
    )
})

export default OrgCreateDetail
